/**
 * CompanyDashboard - Organization dashboard at /company/:slug
 *
 * Displays company overview: stats, radar chart, type distribution,
 * and department/team structure. BtoB-oriented language only
 * (no fortune-telling or astrology references).
 */

import React, { useState, useEffect, useCallback } from 'react';
import styled, { keyframes } from 'styled-components';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';
import companyService from '../../services/companyService';
import RadarChart from '../../components/company/RadarChart';
import TypeDistribution from '../../components/company/TypeDistribution';
import TeamCard from '../../components/company/TeamCard';
import StatCard from '../../components/company/StatCard';
import PricingBanner from '../../components/company/PricingBanner';
import SpectrumBar from '../../components/company/SpectrumBar';

/* ================================================================== */
/*  Constants                                                          */
/* ================================================================== */

const AXIS_KEYS = ['activation', 'judgment', 'choice', 'resonance', 'awareness'];

/* ================================================================== */
/*  Keyframes                                                          */
/* ================================================================== */

const shimmer = keyframes`
  0% { background-position: -400px 0; }
  100% { background-position: 400px 0; }
`;

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(12px); }
  to   { opacity: 1; transform: translateY(0); }
`;

/* ================================================================== */
/*  Styled components                                                  */
/* ================================================================== */

/* ---- Page shell ---- */

const PageContainer = styled.div`
  max-width: 960px;
  margin: 0 auto;
  padding: 48px 24px 80px;
  animation: ${fadeIn} 0.35s ease-out;

  @media (max-width: ${(props) => props.theme.breakpoints?.mobile || '640px'}) {
    padding: 24px 16px 64px;
  }
`;

/* ---- Toast ---- */

const toastSlide = keyframes`
  0%   { opacity: 0; transform: translateY(16px); }
  10%  { opacity: 1; transform: translateY(0); }
  90%  { opacity: 1; transform: translateY(0); }
  100% { opacity: 0; transform: translateY(-8px); }
`;

const Toast = styled.div`
  position: fixed;
  bottom: 32px;
  left: 50%;
  transform: translateX(-50%);
  background: ${(props) => props.theme.colors.success || '#10B981'};
  color: #fff;
  padding: 10px 24px;
  border-radius: ${(props) => props.theme.borderRadius?.md || '8px'};
  font-size: 0.85rem;
  font-weight: 600;
  pointer-events: none;
  z-index: 1000;
  animation: ${toastSlide} 2s ease forwards;
`;

/* ---- Header bar ---- */

const HeaderBar = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px 20px;
  margin-bottom: 32px;
`;

const CompanyName = styled.h1`
  font-size: 1.8rem;
  font-weight: 800;
  color: ${(props) => props.theme.colors.text};
  margin: 0;
  font-family: ${(props) => props.theme.fonts?.main};
  line-height: 1.2;

  @media (max-width: ${(props) => props.theme.breakpoints?.mobile || '640px'}) {
    font-size: 1.4rem;
  }
`;

const MemberBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 4px 14px;
  border-radius: ${(props) => props.theme.borderRadius?.full || '9999px'};
  background: ${(props) => props.theme.colors.primaryGlow || 'rgba(99,102,241,0.12)'};
  border: 1px solid ${(props) => props.theme.colors.primary};
  font-family: ${(props) => props.theme.fonts?.mono};
  font-size: 0.8rem;
  font-weight: 600;
  color: ${(props) => props.theme.colors.primary};
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-left: auto;

  @media (max-width: ${(props) => props.theme.breakpoints?.mobile || '640px'}) {
    width: 100%;
    flex-wrap: wrap;
  }
`;

const InviteCodeBox = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 14px;
  border-radius: ${(props) => props.theme.borderRadius?.md || '8px'};
  background: ${(props) => props.theme.colors.bgTertiary};
  border: 1px solid ${(props) => props.theme.colors.border};
  color: ${(props) => props.theme.colors.text};
  font-family: ${(props) => props.theme.fonts?.mono};
  font-size: 0.82rem;
  font-weight: 500;
  cursor: pointer;
  transition: border-color 0.2s, background 0.2s;

  &:hover {
    border-color: ${(props) => props.theme.colors.primary};
    background: ${(props) => props.theme.colors.bgSecondary};
  }
`;

const CopyIcon = styled.span`
  display: inline-flex;
  align-items: center;
  color: ${(props) => props.theme.colors.textSecondary};
  font-size: 14px;
`;

const IconLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: ${(props) => props.theme.borderRadius?.md || '8px'};
  background: ${(props) => props.theme.colors.bgTertiary};
  border: 1px solid ${(props) => props.theme.colors.border};
  color: ${(props) => props.theme.colors.textSecondary};
  text-decoration: none;
  transition: border-color 0.2s, color 0.2s;

  &:hover {
    border-color: ${(props) => props.theme.colors.primary};
    color: ${(props) => props.theme.colors.primary};
  }
`;

/* ---- Stats row ---- */

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
  margin-bottom: 32px;

  @media (max-width: ${(props) => props.theme.breakpoints?.tablet || '768px'}) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: ${(props) => props.theme.breakpoints?.mobile || '640px'}) {
    grid-template-columns: 1fr;
  }
`;

/* ---- Visualization area ---- */

const VisualizationGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
  margin-bottom: 40px;

  @media (max-width: ${(props) => props.theme.breakpoints?.tablet || '768px'}) {
    grid-template-columns: 1fr;
  }
`;

const ChartPanel = styled.div`
  background: ${(props) => props.theme.colors.bgSecondary};
  border: 1px solid ${(props) => props.theme.colors.border};
  border-radius: ${(props) => props.theme.borderRadius?.lg || '12px'};
  padding: 24px;
`;

const PanelTitle = styled.h2`
  font-size: 0.9rem;
  font-weight: 600;
  color: ${(props) => props.theme.colors.textSecondary};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin: 0 0 20px 0;
`;

const ChartCenter = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

/* ---- Departments / Teams section ---- */

const SectionTitle = styled.h2`
  font-size: 1.2rem;
  font-weight: 700;
  color: ${(props) => props.theme.colors.text};
  margin: 0 0 20px 0;
`;

const SectionTabBar = styled.div`
  display: flex;
  gap: 0;
  border-bottom: 1px solid ${(props) => props.theme.colors.border};
  margin-bottom: 20px;
`;

const SectionTab = styled.button`
  padding: 8px 18px;
  background: transparent;
  border: none;
  border-bottom: 2px solid ${(props) => (props.$active ? props.theme.colors.primary : 'transparent')};
  color: ${(props) => (props.$active ? props.theme.colors.text : props.theme.colors.textSecondary)};
  font-size: 0.88rem;
  font-weight: ${(props) => (props.$active ? 700 : 500)};
  cursor: pointer;
  transition: color 0.2s, border-color 0.2s;
  white-space: nowrap;

  &:hover {
    color: ${(props) => props.theme.colors.text};
  }
`;

const DeptHeader = styled.h3`
  font-size: 0.95rem;
  font-weight: 600;
  color: ${(props) => props.theme.colors.textSecondary};
  margin: 24px 0 12px 0;
  padding-bottom: 8px;
  border-bottom: 1px solid ${(props) => props.theme.colors.border};
  display: flex;
  align-items: center;
  gap: 8px;
`;

const DeptMemberCount = styled.span`
  font-size: 0.78rem;
  font-weight: 400;
  color: ${(props) => props.theme.colors.textMuted || props.theme.colors.textSecondary};
  opacity: 0.7;
`;

const TeamsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 16px;
  margin-bottom: 16px;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 48px 24px;
  color: ${(props) => props.theme.colors.textSecondary};
  background: ${(props) => props.theme.colors.bgSecondary};
  border: 1px dashed ${(props) => props.theme.colors.border};
  border-radius: ${(props) => props.theme.borderRadius?.lg || '12px'};
  font-size: 0.9rem;
  line-height: 1.6;
`;

/* ---- AI Analysis Section ---- */

const AiSection = styled.div`
  margin-bottom: 40px;
`;

const AiButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  border-radius: ${(props) => props.theme.borderRadius?.md || '8px'};
  background: linear-gradient(135deg, ${(props) => props.theme.colors.primary}, ${(props) => props.theme.colors.secondary || '#7C3AED'});
  color: #fff;
  border: none;
  font-weight: 600;
  font-size: 0.9rem;
  cursor: pointer;
  transition: opacity 0.2s, transform 0.15s;
  margin-bottom: 20px;

  &:hover:not(:disabled) {
    opacity: 0.9;
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const AiResultGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;

  @media (max-width: ${(props) => props.theme.breakpoints?.tablet || '768px'}) {
    grid-template-columns: 1fr;
  }
`;

const AiCard = styled.div`
  background: ${(props) => props.theme.colors.bgSecondary};
  border: 1px solid ${(props) => props.theme.colors.border};
  border-radius: ${(props) => props.theme.borderRadius?.lg || '12px'};
  padding: 20px;
`;

const AiCardTitle = styled.h3`
  font-size: 0.85rem;
  font-weight: 700;
  color: ${(props) => props.$color || props.theme.colors.primary};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin: 0 0 12px 0;
  display: flex;
  align-items: center;
  gap: 6px;
`;

const AiList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const AiListItem = styled.li`
  font-size: 0.85rem;
  line-height: 1.6;
  color: ${(props) => props.theme.colors.text};
  padding: 6px 0;
  border-bottom: 1px solid ${(props) => props.theme.colors.border}20;

  &:last-child {
    border-bottom: none;
  }
`;

const AiSummary = styled.div`
  grid-column: 1 / -1;
  background: ${(props) => props.theme.colors.bgSecondary};
  border: 1px solid ${(props) => props.theme.colors.primary}40;
  border-radius: ${(props) => props.theme.borderRadius?.lg || '12px'};
  padding: 20px;
  font-size: 0.9rem;
  line-height: 1.7;
  color: ${(props) => props.theme.colors.text};
`;

/* ---- Key Persons / Role Aptitudes ---- */

const KeyPersonsGrid = styled.div`
  grid-column: 1 / -1;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 16px;
`;

const KeyPersonCard = styled.div`
  background: ${(props) => props.theme.colors.bgSecondary};
  border: 1px solid ${(props) => props.theme.colors.primary}30;
  border-radius: ${(props) => props.theme.borderRadius?.lg || '12px'};
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const KeyPersonName = styled.div`
  font-size: 0.9rem;
  font-weight: 700;
  color: ${(props) => props.theme.colors.text};
  display: flex;
  align-items: center;
  gap: 6px;
`;

const KeyPersonTendency = styled.div`
  font-size: 0.82rem;
  line-height: 1.5;
  color: ${(props) => props.theme.colors.textSecondary};
`;

const KeyPersonReason = styled.div`
  font-size: 0.82rem;
  line-height: 1.5;
  color: ${(props) => props.theme.colors.text};
  padding-top: 4px;
  border-top: 1px solid ${(props) => props.theme.colors.border}30;
`;

const RoleAptitudesSection = styled.div`
  grid-column: 1 / -1;
`;

const RolePersonRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 10px 0;
  border-bottom: 1px solid ${(props) => props.theme.colors.border}20;
  &:last-child { border-bottom: none; }
`;

const RolePersonName = styled.span`
  font-size: 0.85rem;
  font-weight: 600;
  color: ${(props) => props.theme.colors.text};
  min-width: 120px;
  flex-shrink: 0;
`;

const RoleTags = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;

const RoleTag = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 3px 10px;
  border-radius: ${(props) => props.theme.borderRadius?.full || '9999px'};
  font-size: 0.75rem;
  font-weight: 600;
  background: ${(props) => props.theme.colors.primaryGlow || 'rgba(99,102,241,0.12)'};
  color: ${(props) => props.theme.colors.primary};
  cursor: default;
  position: relative;

  &:hover::after {
    content: attr(title);
    position: absolute;
    bottom: calc(100% + 6px);
    left: 50%;
    transform: translateX(-50%);
    background: ${(props) => props.theme.colors.bgPrimary};
    border: 1px solid ${(props) => props.theme.colors.border};
    padding: 6px 10px;
    border-radius: 6px;
    font-size: 0.75rem;
    font-weight: 400;
    color: ${(props) => props.theme.colors.text};
    white-space: nowrap;
    max-width: 300px;
    z-index: 10;
    pointer-events: none;
    box-shadow: 0 4px 12px rgba(0,0,0,0.15);
  }
`;

const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.4; }
`;

const AiLoadingText = styled.span`
  animation: ${pulse} 1.5s ease-in-out infinite;
`;

/* ---- Loading skeleton ---- */

const SkeletonBlock = styled.div`
  background: linear-gradient(
    90deg,
    ${(props) => props.theme.colors.bgTertiary} 25%,
    ${(props) => props.theme.colors.bgSecondary} 50%,
    ${(props) => props.theme.colors.bgTertiary} 75%
  );
  background-size: 800px 100%;
  animation: ${shimmer} 1.6s infinite;
  border-radius: ${(props) => props.theme.borderRadius?.md || '8px'};
  height: ${(props) => props.$h || '20px'};
  width: ${(props) => props.$w || '100%'};
  margin-bottom: ${(props) => props.$mb || '0'};
`;

const SkeletonStatsGrid = styled(StatsGrid)``;

const SkeletonCard = styled.div`
  background: ${(props) => props.theme.colors.bgTertiary};
  border: 1px solid ${(props) => props.theme.colors.border};
  border-radius: ${(props) => props.theme.borderRadius?.md || '8px'};
  padding: 24px;
`;

/* ---- Error state ---- */

const ErrorContainer = styled.div`
  text-align: center;
  padding: 64px 24px;
`;

const ErrorTitle = styled.h2`
  font-size: 1.3rem;
  font-weight: 700;
  color: ${(props) => props.theme.colors.text};
  margin: 0 0 12px 0;
`;

const ErrorMessage = styled.p`
  font-size: 0.9rem;
  color: ${(props) => props.theme.colors.textSecondary};
  margin: 0 0 24px 0;
`;

const RetryButton = styled.button`
  padding: 10px 24px;
  border-radius: ${(props) => props.theme.borderRadius?.md || '8px'};
  background: ${(props) => props.theme.colors.primary};
  color: #fff;
  border: none;
  font-weight: 600;
  font-size: 0.9rem;
  cursor: pointer;
  transition: background 0.2s;

  &:hover {
    background: ${(props) => props.theme.colors.secondary};
  }
`;

/* ================================================================== */
/*  SVG Icons (inline)                                                 */
/* ================================================================== */

const GearIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="3" />
    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
  </svg>
);

const UsersIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
  </svg>
);

const ClipboardIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
  </svg>
);

/* ================================================================== */
/*  Helper: axis name map                                              */
/* ================================================================== */

function useAxisNames() {
  const { t } = useLanguage();
  return {
    activation: t('行動特性', 'Behavioral'),
    judgment: t('判断基準', 'Decision'),
    choice: t('価値観', 'Values'),
    resonance: t('共感性', 'Empathy'),
    awareness: t('自己認識', 'Self-Awareness'),
  };
}

/* ================================================================== */
/*  Loading skeleton sub-component                                     */
/* ================================================================== */

function LoadingSkeleton() {
  return (
    <PageContainer>
      <HeaderBar>
        <SkeletonBlock $h="32px" $w="220px" $mb="0" />
        <SkeletonBlock $h="26px" $w="120px" $mb="0" />
      </HeaderBar>

      <SkeletonStatsGrid>
        {[0, 1, 2, 3].map((i) => (
          <SkeletonCard key={i}>
            <SkeletonBlock $h="14px" $w="60%" $mb="12px" />
            <SkeletonBlock $h="32px" $w="80px" $mb="8px" />
            <SkeletonBlock $h="12px" $w="40%" />
          </SkeletonCard>
        ))}
      </SkeletonStatsGrid>

      <VisualizationGrid>
        <SkeletonCard style={{ minHeight: 300 }}>
          <SkeletonBlock $h="14px" $w="50%" $mb="20px" />
          <SkeletonBlock $h="200px" $w="200px" style={{ margin: '0 auto', borderRadius: '50%' }} />
        </SkeletonCard>
        <SkeletonCard style={{ minHeight: 300 }}>
          <SkeletonBlock $h="14px" $w="50%" $mb="20px" />
          {[0, 1, 2, 3, 4].map((i) => (
            <SkeletonBlock key={i} $h="22px" $w="100%" $mb="10px" />
          ))}
        </SkeletonCard>
      </VisualizationGrid>
    </PageContainer>
  );
}

/* ================================================================== */
/*  Main component                                                     */
/* ================================================================== */

function CompanyDashboard() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { t, lang } = useLanguage();
  const { user, isAuthenticated } = useAuth();

  /* ---- State ---- */
  const [company, setCompany] = useState(null);
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [toastText, setToastText] = useState('');
  const [aiResult, setAiResult] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState(null);
  const [sectionView, setSectionView] = useState('teams'); // 'teams' | 'positions'

  /* ---- Axis names (translated) ---- */
  const axisNames = useAxisNames();

  const [aiCachedAt, setAiCachedAt] = useState(null);

  /* ---- Data fetching ---- */
  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [companyData, analysisData, cachedAi] = await Promise.all([
        companyService.getCompany(slug),
        companyService.getAnalysis(slug).catch(() => null),
        companyService.getCachedAiAnalysis(slug).catch(() => null),
      ]);
      setCompany(companyData);
      setAnalysis(analysisData);
      if (cachedAi && cachedAi.cached && cachedAi.result) {
        setAiResult(cachedAi.result);
        setAiCachedAt(cachedAi.created_at);
      }
    } catch (err) {
      setError(err.message || t('データの取得に失敗しました', 'Failed to fetch data'));
    } finally {
      setLoading(false);
    }
  }, [slug, t]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  /* ---- AI Analysis ---- */
  const runAiAnalysis = useCallback(async () => {
    setAiLoading(true);
    setAiError(null);
    try {
      const result = await companyService.getAiAnalysis(slug);
      setAiResult(result);
      setAiCachedAt(new Date().toISOString());
    } catch (err) {
      setAiError(err.message || t('AI分析に失敗しました', 'AI analysis failed'));
    } finally {
      setAiLoading(false);
    }
  }, [slug, t]);

  /* ---- Clipboard helper ---- */
  const copyInviteCode = useCallback(() => {
    if (!company?.invite_code) return;
    navigator.clipboard.writeText(company.invite_code).then(() => {
      setToastText(t('招待コードをコピーしました', 'Invite code copied'));
      setTimeout(() => setToastText(''), 2200);
    }).catch(() => {
      // Fallback for older browsers
      const textarea = document.createElement('textarea');
      textarea.value = company.invite_code;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      setToastText(t('招待コードをコピーしました', 'Invite code copied'));
      setTimeout(() => setToastText(''), 2200);
    });
  }, [company, t]);

  /* ---- Derived values ---- */
  const isAdmin = company?.members?.some(
    (m) => m.user_id === user?.id && m.role === 'admin'
  ) ?? false;

  const totalMembers = company?.member_count ?? company?.members?.length ?? 0;
  const diagnosedMembers = company?.diagnosed_count ?? (
    company?.members?.filter((m) => m.has_diagnosis)?.length ?? 0
  );

  const nearestType = analysis?.nearest_type?.type_code || '--';

  const strongestAxis = analysis?.axis_balance?.strongest_axis
    ? axisNames[analysis.axis_balance.strongest_axis] || analysis.axis_balance.strongest_axis
    : '--';

  const weakestAxis = analysis?.axis_balance?.weakest_axis
    ? axisNames[analysis.axis_balance.weakest_axis] || analysis.axis_balance.weakest_axis
    : '--';

  /* ---- Radar chart data ---- */
  const radarData = analysis?.centroid
    ? AXIS_KEYS.map((key) => ({
        label: axisNames[key],
        value: Math.min(Math.max(analysis.centroid[key] ?? 0, 0), 1),
      }))
    : [];

  /* ---- Departments & teams ---- */
  const departments = company?.departments || [];
  const allTeams = company?.teams || [];

  // Group teams by department
  const teamsByDept = {};
  const unassignedTeams = [];
  allTeams.forEach((team) => {
    if (team.department_id) {
      if (!teamsByDept[team.department_id]) {
        teamsByDept[team.department_id] = [];
      }
      teamsByDept[team.department_id].push(team);
    } else {
      unassignedTeams.push(team);
    }
  });

  // Build team analysis lookup from analysis.teams if available
  const teamAnalysisMap = {};
  if (analysis?.teams) {
    analysis.teams.forEach((ta) => {
      teamAnalysisMap[ta.team_id] = ta;
    });
  }

  // Build department analysis lookup from analysis.departments if available
  const deptAnalysisMap = {};
  if (analysis?.departments) {
    analysis.departments.forEach((da) => {
      deptAnalysisMap[da.department_id] = da;
    });
  }

  // Count members per department
  const membersByDept = {};
  (company?.members || []).forEach((m) => {
    const deptId = m.department_id;
    if (deptId) {
      if (!membersByDept[deptId]) membersByDept[deptId] = 0;
      membersByDept[deptId]++;
    }
  });

  // Position analysis from analysis.positions
  const positionAnalysisList = analysis?.positions || [];

  /* ================================================================ */
  /*  Render states                                                    */
  /* ================================================================ */

  if (!isAuthenticated) {
    return (
      <PageContainer>
        <ErrorContainer>
          <ErrorTitle>{t('ログインが必要です', 'Login Required')}</ErrorTitle>
          <ErrorMessage>
            {t(
              '組織ダッシュボードを利用するにはログインまたは新規登録が必要です。',
              'Please log in or sign up to access the organization dashboard.'
            )}
          </ErrorMessage>
          <RetryButton onClick={() => navigate('/')}>
            {t('ログイン / 新規登録', 'Login / Sign Up')}
          </RetryButton>
        </ErrorContainer>
      </PageContainer>
    );
  }

  if (loading) {
    return <LoadingSkeleton />;
  }

  if (error) {
    const isAuthError = error === 'Not authenticated' || error === 'Invalid or expired token';
    return (
      <PageContainer>
        <ErrorContainer>
          <ErrorTitle>
            {isAuthError
              ? t('ログインが必要です', 'Login Required')
              : t('エラーが発生しました', 'An Error Occurred')}
          </ErrorTitle>
          <ErrorMessage>
            {isAuthError
              ? t('組織ダッシュボードを利用するにはログインまたは新規登録が必要です。', 'Please log in or sign up to access the organization dashboard.')
              : error}
          </ErrorMessage>
          <RetryButton onClick={isAuthError ? () => navigate('/') : fetchData}>
            {isAuthError
              ? t('ログイン / 新規登録', 'Login / Sign Up')
              : t('再試行', 'Retry')}
          </RetryButton>
        </ErrorContainer>
      </PageContainer>
    );
  }

  if (!company) {
    return (
      <PageContainer>
        <EmptyState>
          {t('組織が見つかりませんでした。', 'Organization not found.')}
        </EmptyState>
      </PageContainer>
    );
  }

  /* ================================================================ */
  /*  Main render                                                      */
  /* ================================================================ */

  return (
    <PageContainer>
      {/* ---- Toast notification ---- */}
      {toastText && <Toast>{toastText}</Toast>}

      {/* ──────────────── Header Bar ──────────────── */}
      <HeaderBar>
        <CompanyName>{company.name}</CompanyName>

        <MemberBadge>
          {diagnosedMembers}/{totalMembers} {t('診断完了', 'diagnosed')}
        </MemberBadge>

        <HeaderActions>
          {company.invite_code && (
            <InviteCodeBox onClick={copyInviteCode} title={t('クリックしてコピー', 'Click to copy')}>
              <span>{company.invite_code}</span>
              <CopyIcon><ClipboardIcon /></CopyIcon>
            </InviteCodeBox>
          )}

          <IconLink to={`/company/${slug}/members`} title={t('メンバー一覧', 'Members')}>
            <UsersIcon />
          </IconLink>

          {isAdmin && (
            <IconLink to={`/company/${slug}/settings`} title={t('設定', 'Settings')}>
              <GearIcon />
            </IconLink>
          )}
        </HeaderActions>
      </HeaderBar>

      {/* ──────────────── Pricing Banner (free tier exceeded) ──────────────── */}
      <PricingBanner slug={slug} activeMembers={totalMembers} isAdmin={isAdmin} />

      {/* ──────────────── Stats Cards ──────────────── */}
      <StatsGrid>
        <StatCard
          title={t('メンバー', 'Members')}
          value={`${diagnosedMembers} / ${totalMembers}`}
          subtitle={t('行動特性分析 完了数', 'Behavioral analysis completed')}
        />
        <StatCard
          title={t('組織タイプ', 'Organization Type')}
          value={nearestType}
          subtitle={t('統計的分析に基づく分類', 'Based on statistical analysis')}
        />
        <StatCard
          title={t('強み', 'Strength')}
          value={strongestAxis}
          subtitle={t('組織の優位特性', 'Dominant organizational trait')}
        />
        <StatCard
          title={t('弱み', 'Weakness')}
          value={weakestAxis}
          subtitle={t('成長ポテンシャル', 'Growth potential')}
        />
      </StatsGrid>

      {/* ──────────────── Visualization Area ──────────────── */}
      <VisualizationGrid>
        {/* Left: Radar Chart */}
        <ChartPanel>
          <PanelTitle>{t('組織レーダーチャート', 'Organization Radar Chart')}</PanelTitle>
          {radarData.length === 5 ? (
            <ChartCenter>
              <RadarChart data={radarData} size={300} />
            </ChartCenter>
          ) : (
            <EmptyState>
              {t(
                '分析データがまだありません。メンバーの行動特性分析が完了すると表示されます。',
                'No analysis data yet. This will be displayed once members complete their behavioral analysis.'
              )}
            </EmptyState>
          )}
        </ChartPanel>

        {/* Center: Structure Spectrum */}
        <ChartPanel>
          <PanelTitle>{t('構造スペクトラム', 'Structure Spectrum')}</PanelTitle>
          <SpectrumBar axes={analysis?.centroid || {}} normalize lang={lang} />
        </ChartPanel>

        {/* Right: Type Distribution */}
        <ChartPanel>
          <PanelTitle>{t('タイプ分布', 'Type Distribution')}</PanelTitle>
          {analysis?.type_distribution && analysis.type_distribution.length > 0 ? (
            <TypeDistribution data={analysis.type_distribution} />
          ) : (
            <EmptyState>
              {t(
                'タイプ分布データがありません。メンバーの分析が完了すると表示されます。',
                'No type distribution data. This will be displayed once members are analyzed.'
              )}
            </EmptyState>
          )}
        </ChartPanel>
      </VisualizationGrid>

      {/* ──────────────── AI Analysis ──────────────── */}
      <AiSection>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '8px 16px', marginBottom: 16 }}>
          <SectionTitle style={{ margin: 0 }}>{t('AI組織分析', 'AI Organization Analysis')}</SectionTitle>
          <Link
            to={`/company/${slug}/ai-report`}
            style={{
              fontSize: '0.82rem',
              fontWeight: 600,
              color: 'inherit',
              textDecoration: 'none',
              padding: '6px 14px',
              borderRadius: '8px',
              border: `1px solid ${company ? 'currentColor' : 'transparent'}`,
              opacity: 0.7,
              transition: 'opacity 0.2s',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.opacity = '1'; }}
            onMouseLeave={(e) => { e.currentTarget.style.opacity = '0.7'; }}
          >
            {t('AI組織レポート全体を見る', 'View Full AI Report')} →
          </Link>
        </div>

        {!aiResult && !aiLoading && (
          <AiButton onClick={runAiAnalysis}>
            <span>&#x2728;</span>
            {t('AIで組織を分析する', 'Analyze with AI')}
          </AiButton>
        )}

        {!aiResult && aiLoading && (
          <AiButton disabled>
            <AiLoadingText>{t('分析中...（30秒〜1分ほどかかります）', 'Analyzing... (30s-1min)')}</AiLoadingText>
          </AiButton>
        )}

        {aiError && !aiLoading && (
          <EmptyState style={{ marginBottom: 16 }}>
            {aiError}
            <br />
            <AiButton onClick={runAiAnalysis} style={{ marginTop: 12 }}>
              {t('再試行', 'Retry')}
            </AiButton>
          </EmptyState>
        )}

        {aiResult && (
          <div style={{ position: 'relative' }}>
            {aiLoading && (
              <div style={{
                position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
                background: 'rgba(0,0,0,0.6)', borderRadius: '12px', zIndex: 10,
                display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '12px',
              }}>
                <div style={{ width: '32px', height: '32px', border: '3px solid rgba(255,255,255,0.2)', borderTopColor: '#fff', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
                <span style={{ color: '#fff', fontSize: '0.9rem', fontWeight: 600 }}>
                  {t('再分析中...', 'Re-analyzing...')}
                </span>
                <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
              </div>
            )}
            <AiResultGrid>
              <AiSummary>
                <AiCardTitle>{t('組織概要', 'Organization Overview')}</AiCardTitle>
                {aiResult.overview || aiResult.summary}
              </AiSummary>

              <AiCard>
                <AiCardTitle $color="#10B981">{t('構造的な強み', 'Structural Strengths')}</AiCardTitle>
                <AiList>
                  {(aiResult.structural_strengths || aiResult.strengths || []).map((item, i) => (
                    <AiListItem key={i}>{item}</AiListItem>
                  ))}
                </AiList>
              </AiCard>

              <AiCard>
                <AiCardTitle $color="#EF4444">{t('構造的なリスク', 'Structural Risks')}</AiCardTitle>
                <AiList>
                  {(aiResult.structural_risks || aiResult.challenges || []).map((item, i) => (
                    <AiListItem key={i}>{item}</AiListItem>
                  ))}
                </AiList>
              </AiCard>

              <AiCard>
                <AiCardTitle $color="#8B5CF6">{t('役職適性', 'Position Fit')}</AiCardTitle>
                <AiList>
                  {(aiResult.position_fit || []).map((item, i) => (
                    <AiListItem key={i}>{item}</AiListItem>
                  ))}
                </AiList>
              </AiCard>

              <AiCard>
                <AiCardTitle $color="#F59E0B">{t('不足している能力', 'Missing Capabilities')}</AiCardTitle>
                <AiList>
                  {(aiResult.missing_capabilities || aiResult.missing_roles || []).map((item, i) => (
                    <AiListItem key={i}>{item}</AiListItem>
                  ))}
                </AiList>
              </AiCard>

              <AiCard>
                <AiCardTitle $color="#06B6D4">{t('配置提案', 'Team Composition Advice')}</AiCardTitle>
                <AiList>
                  {(aiResult.team_composition_advice || aiResult.cautions || []).map((item, i) => (
                    <AiListItem key={i}>{item}</AiListItem>
                  ))}
                </AiList>
              </AiCard>

              <AiCard style={{ gridColumn: '1 / -1' }}>
                <AiCardTitle $color="#6366F1">{t('検討すべきアクション', 'Action Items')}</AiCardTitle>
                <AiList>
                  {(aiResult.action_items || aiResult.team_building || []).map((item, i) => (
                    <AiListItem key={i}>{item}</AiListItem>
                  ))}
                </AiList>
              </AiCard>

              {aiResult.key_persons?.length > 0 && (
                <>
                  <div style={{ gridColumn: '1 / -1' }}>
                    <AiCardTitle $color="#D946EF">{t('キーパーソン', 'Key Persons')}</AiCardTitle>
                  </div>
                  <KeyPersonsGrid>
                    {aiResult.key_persons.map((person, i) => (
                      <KeyPersonCard key={i}>
                        <KeyPersonName>
                          <span style={{ fontSize: '1rem' }}>&#x1F451;</span>
                          {person.name}
                        </KeyPersonName>
                        <KeyPersonTendency>{person.tendency}</KeyPersonTendency>
                        <KeyPersonReason>{person.reason}</KeyPersonReason>
                      </KeyPersonCard>
                    ))}
                  </KeyPersonsGrid>
                </>
              )}

              {aiResult.role_aptitudes?.length > 0 && (
                <RoleAptitudesSection>
                  <AiCard>
                    <AiCardTitle $color="#0EA5E9">{t('役割適性', 'Role Aptitudes')}</AiCardTitle>
                    {aiResult.role_aptitudes.map((person, i) => (
                      <RolePersonRow key={i}>
                        <RolePersonName>{person.name}</RolePersonName>
                        <RoleTags>
                          {(person.aptitudes || []).map((apt, j) => (
                            <RoleTag key={j} title={apt.reason}>
                              {apt.role}
                            </RoleTag>
                          ))}
                        </RoleTags>
                      </RolePersonRow>
                    ))}
                  </AiCard>
                </RoleAptitudesSection>
              )}

              <div style={{ gridColumn: '1 / -1', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                {aiCachedAt && (
                  <span style={{ fontSize: '0.75rem', opacity: 0.5 }}>
                    {t('分析日時', 'Analyzed')}: {new Date(aiCachedAt).toLocaleString()}
                  </span>
                )}
                <AiButton onClick={runAiAnalysis} disabled={aiLoading} style={{ background: 'none', color: 'inherit', border: '1px solid currentColor', padding: '8px 16px', fontSize: '0.82rem', opacity: 0.7, marginLeft: 'auto' }}>
                  {aiLoading
                    ? t('再分析中...', 'Re-analyzing...')
                    : t('再分析', 'Re-analyze')}
                </AiButton>
              </div>
            </AiResultGrid>
          </div>
        )}
      </AiSection>

      {/* ──────────────── Departments & Teams / Positions ──────────────── */}
      <SectionTitle>{t('組織構成', 'Organization Structure')}</SectionTitle>

      <SectionTabBar>
        <SectionTab
          $active={sectionView === 'teams'}
          onClick={() => setSectionView('teams')}
        >
          {t('部署・チーム', 'Departments & Teams')}
        </SectionTab>
        <SectionTab
          $active={sectionView === 'positions'}
          onClick={() => setSectionView('positions')}
        >
          {t('役職', 'Positions')}
        </SectionTab>
      </SectionTabBar>

      {sectionView === 'teams' && (
        <>
          {departments.length === 0 && allTeams.length === 0 ? (
            <EmptyState>
              {t(
                '部署・チームがまだ登録されていません。設定画面から追加できます。',
                'No departments or teams registered yet. You can add them from the settings page.'
              )}
            </EmptyState>
          ) : (
            <>
              {/* Render departments with their teams */}
              {departments.map((dept) => {
                const deptTeams = teamsByDept[dept.id] || [];
                const deptMemberCount = membersByDept[dept.id] || 0;
                return (
                  <div key={dept.id}>
                    <DeptHeader>
                      {dept.name}
                      <DeptMemberCount>{deptMemberCount} {t('人', 'members')}</DeptMemberCount>
                    </DeptHeader>
                    {deptTeams.length > 0 ? (
                      <TeamsGrid>
                        {deptTeams.map((team) => (
                          <TeamCard
                            key={team.id}
                            team={team}
                            analysis={teamAnalysisMap[team.id] || {}}
                            onClick={() => navigate(`/company/${slug}/teams/${team.id}`)}
                          />
                        ))}
                      </TeamsGrid>
                    ) : (
                      <TeamsGrid>
                        <TeamCard
                          team={{ name: dept.name, member_count: deptMemberCount }}
                          analysis={deptAnalysisMap[dept.id] || {}}
                          onClick={() => navigate(`/company/${slug}/departments/${dept.id}`)}
                        />
                      </TeamsGrid>
                    )}
                  </div>
                );
              })}

              {/* Render unassigned teams (not attached to any department) */}
              {unassignedTeams.length > 0 && (
                <div>
                  {departments.length > 0 && (
                    <DeptHeader>{t('未所属チーム', 'Unassigned Teams')}</DeptHeader>
                  )}
                  <TeamsGrid>
                    {unassignedTeams.map((team) => (
                      <TeamCard
                        key={team.id}
                        team={team}
                        analysis={teamAnalysisMap[team.id] || {}}
                        onClick={() => navigate(`/company/${slug}/teams/${team.id}`)}
                      />
                    ))}
                  </TeamsGrid>
                </div>
              )}
            </>
          )}
        </>
      )}

      {sectionView === 'positions' && (
        <>
          {positionAnalysisList.length === 0 ? (
            <EmptyState>
              {t(
                '役職データがまだありません。メンバーに役職を設定するか、設定画面で役職プリセットを追加してください。',
                'No position data yet. Assign positions to members or add position presets in settings.'
              )}
            </EmptyState>
          ) : (
            <TeamsGrid>
              {positionAnalysisList.map((posData) => (
                <TeamCard
                  key={posData.position}
                  team={{ name: posData.position, member_count: posData.member_count }}
                  analysis={{ centroid: posData.centroid, nearest_type: posData.nearest_type }}
                  onClick={() => navigate(`/company/${slug}/positions/${encodeURIComponent(posData.position)}`)}
                />
              ))}
            </TeamsGrid>
          )}
        </>
      )}
    </PageContainer>
  );
}

export default CompanyDashboard;
